import { NgClass } from '@angular/common';
import {
    Component,
    input,
    OnChanges,
    output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { SidebarService, SidebarState } from '../../services/sidebar.service';

/**
 * Barra lateral (sidebar) com cabeçalho/menu/conteúdo projetados.
 *
 * Pode ser fixa ou flutuante ({@link floating}) e sua visibilidade é controlada
 * por {@link visible}. Mudanças de visibilidade são notificadas ao
 * {@link SidebarService}.
 *
 * Usa `ViewEncapsulation.None` de propósito (estiliza o conteúdo projetado e
 * compartilha as classes `.ai-sidebar*`).
 */
@Component({
    selector: 'ai-sidebar',
    templateUrl: 'sidebar.component.html',
    styleUrls: ['sidebar.component.scss'],
    standalone: true,
    imports: [NgClass],
    encapsulation: ViewEncapsulation.Emulated,
})
export class Sidebar implements OnChanges {
    /** Indica se a sidebar está visível. */
    visible = input<boolean>(false);

    /** Lado em que a sidebar é exibida (`left`/`right`). */
    position = input<string>('left');

    /** Define se a sidebar flutua sobre o conteúdo. */
    floating = input<boolean>(false);

    /**
     * Emitido quando o usuário solicita o fechamento da sidebar tocando no
     * backdrop do modo overlay (mobile/tablet). O host é responsável por
     * atualizar o estado de visibilidade (ex.: `sidebarVisible.set(false)`).
     */
    readonly closeRequested = output<void>();

    constructor(private sidebarService: SidebarService) {}

    public ngOnChanges(changes: SimpleChanges) {
        if (changes['visible']) {
            const state = changes['visible'].currentValue;
            this.sidebarService.notifyStateChanged(
                this,
                state ? SidebarState.Open : SidebarState.Closed,
            );
        }
    }

    protected onMenuClicked(): void {}

    /** Solicita o fechamento da sidebar ao tocar no backdrop (modo overlay). */
    protected onBackdropClick(): void {
        this.closeRequested.emit();
    }
}

/** Área de conteúdo da {@link Sidebar}. */
@Component({
    selector: 'ai-sidebar-content',
    template: '<ng-content></ng-content>',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
})
export class SidebarContent {}

/** Cabeçalho da {@link Sidebar}. */
@Component({
    selector: 'ai-sidebar-header',
    template: '<ng-content></ng-content>',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
})
export class SidebarHeader {}
